import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  Loader2,
  RefreshCw,
  ShieldAlert,
  Search,
  XCircle,
} from "lucide-react";
import { checkAdmin } from "@/lib/admin.functions";
import { getIndexingIssues, type IndexingResult } from "@/lib/search-console.functions";
import { seo } from "@/lib/seo";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/admin/indexing")({
  head: () => ({
    ...seo({
      title: "Indexing Issues — The Quantum Minds Admin",
      description: "Internal console surfacing Search Console coverage and sitemap problems.",
      noindex: true,
    }),
  }),
  component: AdminIndexing,
  errorComponent: ({ error }: { error: unknown }) => (
    <div role="alert" className="p-6 text-sm text-destructive">
      {error instanceof Error ? error.message : String(error)}
    </div>
  ),
});

function when(value: string | null): string {
  if (!value) return "—";
  return new Date(value).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
}

function AdminIndexing() {
  const isAdminFn = useServerFn(checkAdmin);
  const issuesFn = useServerFn(getIndexingIssues);
  const [siteUrl, setSiteUrl] = useState<string | undefined>(undefined);
  const [open, setOpen] = useState<string | null>(null);

  const adminQuery = useQuery({
    queryKey: ["admin-check"],
    queryFn: () => isAdminFn({}),
    retry: false,
    staleTime: 60_000,
  });
  const isAdmin = adminQuery.data?.isAdmin === true;

  const issuesQuery = useQuery<IndexingResult>({
    queryKey: ["admin-indexing", siteUrl ?? ""],
    queryFn: () => issuesFn({ data: { siteUrl, sampleLimit: 25 } }),
    enabled: isAdmin,
    retry: false,
    staleTime: 5 * 60_000,
  });

  if (adminQuery.isLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!isAdmin) {
    const denied = adminQuery.error
      ? (adminQuery.error as Error).message.replace(/^Error:\s*/, "")
      : null;
    return (
      <div className="mx-auto flex min-h-[50vh] w-full max-w-md flex-col items-center justify-center gap-3 p-6 text-center">
        <ShieldAlert className="h-8 w-8 text-destructive" />
        <h1 className="text-lg font-semibold text-foreground">Admins only</h1>
        <p className="text-sm text-muted-foreground">
          This console is restricted to admin accounts, and every request is verified on the
          server. You are signed in without admin access — sign in with your admin account to
          open the indexing report.
        </p>
        {denied && (
          <p className="rounded-xl border border-border/60 bg-card/60 px-3 py-2 font-mono text-[11px] text-muted-foreground">
            {denied === "Unauthorized" ? "No signed-in session was sent with the request." : denied}
          </p>
        )}
      </div>
    );
  }

  const result = issuesQuery.data;

  return (
    <div className="mx-auto w-full max-w-5xl space-y-5 p-4 pb-24">
      <header className="space-y-1">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Search className="h-5 w-5 text-primary" />
            <h1 className="text-xl font-semibold text-foreground">Indexing issues</h1>
          </div>
          <button
            onClick={() => issuesQuery.refetch()}
            disabled={issuesQuery.isFetching}
            className="flex min-h-11 items-center gap-2 rounded-full border border-border/60 bg-card/60 px-4 text-xs font-medium text-foreground disabled:opacity-50"
          >
            <RefreshCw className={cn("h-3.5 w-3.5", issuesQuery.isFetching && "animate-spin")} />
            Refresh
          </button>
        </div>
        <p className="text-sm text-muted-foreground">
          Coverage and sitemap problems reported by Google Search Console. Inspection reads the
          stored index state only — it does not request indexing or a re-crawl.
        </p>
      </header>

      {issuesQuery.isLoading ? (
        <div className="flex flex-col items-center gap-2 py-12">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          <p className="text-xs text-muted-foreground">Inspecting sampled URLs…</p>
        </div>
      ) : issuesQuery.error ? (
        <p className="rounded-2xl border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
          {(issuesQuery.error as Error).message.replace(/^Error:\s*/, "")}
        </p>
      ) : result?.status === "selection_required" ? (
        <div className="space-y-3 rounded-2xl border border-border/60 bg-card/60 p-4">
          <p className="text-sm text-foreground">
            Several verified properties cover this site. Choose one:
          </p>
          <div className="flex flex-wrap gap-2">
            {result.candidates.map((candidate) => (
              <button
                key={candidate}
                onClick={() => setSiteUrl(candidate)}
                className="min-h-11 rounded-full border border-border/60 bg-card/50 px-4 font-mono text-xs text-foreground hover:border-primary/60"
              >
                {candidate}
              </button>
            ))}
          </div>
        </div>
      ) : result?.status === "ok" ? (
        <ReportView
          report={result.report}
          open={open}
          setOpen={setOpen}
          onPickProperty={setSiteUrl}
        />
      ) : null}
    </div>
  );
}

function ReportView({
  report,
  open,
  setOpen,
  onPickProperty,
}: {
  report: Extract<IndexingResult, { status: "ok" }>["report"];
  open: string | null;
  setOpen: (value: string | null) => void;
  onPickProperty: (value: string) => void;
}) {
  const errorCount = report.groups
    .filter((g) => g.severity === "error")
    .reduce((t, g) => t + g.count, 0);
  const warningCount = report.groups
    .filter((g) => g.severity === "warning")
    .reduce((t, g) => t + g.count, 0);
  const okCount = report.groups.filter((g) => g.severity === "ok").reduce((t, g) => t + g.count, 0);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <span className="rounded-full border border-border/60 bg-card/50 px-3 py-1 font-mono">
          {report.siteUrl}
        </span>
        <span>checked {when(report.checkedAt)}</span>
        {report.candidates.length > 1 &&
          report.candidates
            .filter((c) => c !== report.siteUrl)
            .map((c) => (
              <button
                key={c}
                onClick={() => onPickProperty(c)}
                className="rounded-full border border-border/60 px-3 py-1 font-mono hover:text-foreground"
              >
                switch to {c}
              </button>
            ))}
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Coverage errors" value={errorCount} tone="error" />
        <Stat label="Warnings" value={warningCount} tone="warning" />
        <Stat label="Indexed (sampled)" value={okCount} tone="ok" />
        <Stat
          label="Sitemap errors"
          value={report.sitemapErrors}
          tone={report.sitemapErrors > 0 ? "error" : "ok"}
        />
      </div>

      <section className="space-y-2">
        <h2 className="text-sm font-semibold text-foreground">Coverage problems</h2>
        <p className="text-xs text-muted-foreground">
          Sampled {report.inspected} of {report.sampleOf} sitemap URLs.
        </p>
        {report.groups.length === 0 ? (
          <p className="rounded-2xl border border-border/60 bg-card/50 p-6 text-center text-sm text-muted-foreground">
            Nothing inspected yet.
          </p>
        ) : (
          <ul className="space-y-2">
            {report.groups.map((group) => {
              const expanded = open === group.key;
              return (
                <li
                  key={group.key}
                  className="overflow-hidden rounded-2xl border border-border/60 bg-card/60"
                >
                  <button
                    onClick={() => setOpen(expanded ? null : group.key)}
                    aria-expanded={expanded}
                    className="flex min-h-11 w-full items-center gap-3 px-4 py-3 text-left"
                  >
                    <SeverityIcon severity={group.severity} />
                    <span className="min-w-0 flex-1 truncate text-sm text-foreground">
                      {group.label}
                    </span>
                    <span
                      className={cn(
                        "rounded-full px-2.5 py-0.5 text-[11px] font-semibold",
                        group.severity === "error" && "bg-destructive/15 text-destructive",
                        group.severity === "warning" && "bg-amber-500/15 text-amber-500",
                        group.severity === "ok" && "bg-emerald-500/15 text-emerald-500",
                      )}
                    >
                      {group.count}
                    </span>
                    <ChevronDown
                      className={cn(
                        "h-4 w-4 shrink-0 text-muted-foreground transition-transform",
                        expanded && "rotate-180",
                      )}
                    />
                  </button>
                  {expanded && (
                    <ul className="space-y-1 border-t border-border/60 px-4 py-3">
                      {group.urls.map((url) => (
                        <li key={url} className="min-w-0">
                          <a
                            href={url}
                            target="_blank"
                            rel="noreferrer"
                            className="block truncate font-mono text-[11px] text-muted-foreground hover:text-primary"
                          >
                            {url}
                          </a>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section className="space-y-2">
        <h2 className="text-sm font-semibold text-foreground">Sitemaps</h2>
        {report.sitemaps.length === 0 ? (
          <p className="rounded-2xl border border-border/60 bg-card/50 p-6 text-center text-sm text-muted-foreground">
            No sitemap submitted for this property.
          </p>
        ) : (
          <ul className="space-y-2">
            {report.sitemaps.map((sitemap) => (
              <li
                key={sitemap.path}
                className="rounded-2xl border border-border/60 bg-card/60 p-4 text-xs"
              >
                <p className="truncate font-mono text-[11px] text-foreground">{sitemap.path}</p>
                <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1.5 sm:grid-cols-3">
                  <Field
                    label="Errors"
                    value={String(sitemap.errors)}
                    warn={sitemap.errors > 0}
                  />
                  <Field
                    label="Warnings"
                    value={String(sitemap.warnings)}
                    warn={sitemap.warnings > 0}
                  />
                  <Field label="Submitted URLs" value={String(sitemap.submitted)} />
                  <Field label="Indexed URLs" value={String(sitemap.indexed)} />
                  <Field label="Last submitted" value={when(sitemap.lastSubmitted)} />
                  <Field label="Last downloaded" value={when(sitemap.lastDownloaded)} />
                </dl>
                {sitemap.errors > 0 && (
                  <p className="mt-2 text-[11px] text-amber-500">
                    Search Console reports errors for this sitemap but does not return the cause
                    through the API — open Sitemaps in Search Console for details.
                  </p>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function SeverityIcon({ severity }: { severity: "error" | "warning" | "ok" }) {
  if (severity === "error") return <XCircle className="h-4 w-4 shrink-0 text-destructive" />;
  if (severity === "warning")
    return <AlertTriangle className="h-4 w-4 shrink-0 text-amber-500" />;
  return <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500" />;
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: "error" | "warning" | "ok";
}) {
  return (
    <div className="rounded-2xl border border-border/60 bg-card/60 p-3">
      <p
        className={cn(
          "text-2xl font-semibold",
          tone === "error" && "text-destructive",
          tone === "warning" && "text-amber-500",
          tone === "ok" && "text-emerald-500",
        )}
      >
        {value}
      </p>
      <p className="mt-0.5 text-[11px] text-muted-foreground">{label}</p>
    </div>
  );
}

function Field({ label, value, warn }: { label: string; value: string; warn?: boolean }) {
  return (
    <div className="min-w-0">
      <dt className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className={cn("truncate text-xs", warn ? "text-destructive" : "text-foreground")}>
        {value}
      </dd>
    </div>
  );
}
